import { Box, Button, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  sxButtons,
  sxLabel,
  sxStyleBtn,
  sxTextField,
  sxTitle,
  sxValidationMessage,
} from "./sxProps";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { ModalDefault } from "../../../../components/ModalDefault";
import TypographyAccessibility from "../../../../components/Accessibility/TypographyAccessibility";
import { cellphoneMask } from "../../../../utils/PhoneRegex";
import { UserData } from "../../../GestaoDeCronicos/PrimeiroAgendamento/AgendarAtendimento/ConfirmarDados";
import DefaultButton from "../../../../components/Accessibility/DefaultButton";
import { useAccessibility } from "../../../../Store/contexts/AccessibilityContext";
import { atualizarComunicacao } from "../../../../services/BeneficiarioService";
import LoadingApp from "../../../../components/LoadingApp";

interface ContatoModalProps {
  open: boolean;
  onClose: () => void;
  userData: UserData;
  openModalFeedBack: (title: string, subtitle: string) => void;
  openModalSuccessNoButton: () => void;
}

export const ModalEditContact: React.FC<ContatoModalProps> = ({
  open,
  onClose,
  userData,
  openModalFeedBack,
  openModalSuccessNoButton,
}) => {
  const accessibility = useAccessibility();
  const [loading, setLoading] = useState<boolean>(false);

  const formik = useFormik({
    initialValues: userData,
    validationSchema: Yup.object({
      cel: Yup.string()
        .required("Celular é obrigatório")
        .matches(/^\(\d{2}\)\s\d{5}-\d{4}$/, "Verifique se o telefone está correto, com 11 dígitos"),
      tel: Yup.string()
        .required("Telefone é obrigatório")
        .matches(/^\(\d{2}\)\s\d{4}-\d{4}$/, "Verifique se o telefone está correto, com 11 dígitos"),
      email: Yup.string()
        .email("Verifique se o e-mail está correto, com @ e ponto")
        .required("E-mail é obrigatório"),
    }),
    onSubmit: (values) => {
      handleUpdateEnderComunic(values);
    },
  });

  useEffect(() => {
    formik.setValues(userData);
  }, [open]);

  const handleUpdateEnderComunic = async ({ cel, email, tel }: UserData) => {
    setLoading(true);
    try {
      const response = await atualizarComunicacao(cel, tel, email);
      if (response?.status === 200) {
        response.data !== "OK"
          ? openModalFeedBack("Erro", response.data)
          : openModalSuccessNoButton();
      } else {
        openModalFeedBack(
          "Erro",
          "Ocorreu um erro ao salvar os dados, tente novamente em instantes."
        );
      }
    } catch (error) {
      openModalFeedBack(
        "Erro",
        "Falha na comunicação com o servidor. Tente novamente mais tarde."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingApp showLoading={loading} />
      <ModalDefault
        open={open}
        title="Atualizar dados do contato"
        sxTitle={sxTitle}
        onClose={onClose}
        maxWidth={578}
      >
        <Box
          style={{
            ...accessibility.textStyleAccessibility,
            ...accessibility.contrastBorderAccessibility,
          }}
        >
          <form onSubmit={formik.handleSubmit}>
            <div className="flex-col gap-20" style={{ marginBottom: "30px" }}>
              <div className="flex-col">
                <TypographyAccessibility component="label" sx={sxLabel}>
                  Telefone
                </TypographyAccessibility>
                <TextField
                  id="tel"
                  type="tel"
                  variant="outlined"
                  onChange={(e) =>
                    formik.setFieldValue("tel", cellphoneMask(e.target.value))
                  }
                  value={formik.values.tel}
                  sx={sxTextField}
                  fullWidth
                  error={formik.touched.tel && Boolean(formik.errors.tel)}
                  InputProps={{
                    style: { ...accessibility.contrastStyle },
                  }}
                  inputProps={{
                    maxLength: 14,
                    style: { color: accessibility.contrastMode ? "white" : "" },
                    'aria-describedby': formik.errors.tel ? 'tel-error' : undefined,
                  }}
                />
                {formik.touched.tel && formik.errors.tel && (
                  <div className="container-validation-message" id="tel-error">
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      size="sm"
                      color="#D84141"
                    />
                    <Typography
                      style={accessibility.textStyleAccessibility}
                      id="errorTel"
                      sx={sxValidationMessage}
                    >
                      {formik.errors.tel}
                    </Typography>
                  </div>
                )}
              </div>

              <div className="flex-col">
                <TypographyAccessibility component="label" sx={sxLabel}>
                  Celular
                </TypographyAccessibility>
                <TextField
                  id="cel"
                  type="tel"
                  variant="outlined"
                  onChange={(e) =>
                    formik.setFieldValue("cel", cellphoneMask(e.target.value))
                  }
                  value={formik.values.cel}
                  sx={sxTextField}
                  fullWidth
                  error={formik.touched.cel && Boolean(formik.errors.cel)}
                  InputProps={{
                    style: { ...accessibility.contrastStyle },
                  }}
                  inputProps={{
                    maxLength: 15,
                    style: { color: accessibility.contrastMode ? "white" : "" },
                    'aria-describedby': formik.errors.cel ? 'cel-error' : undefined,
                  }}
                />
                {formik.touched.cel && formik.errors.cel && (
                  <div className="container-validation-message" id="cel-error">
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      size="sm"
                      color="#D84141"
                    />
                    <Typography
                      style={accessibility.textStyleAccessibility}
                      id="errorCel"
                      sx={sxValidationMessage}
                    >
                      {formik.errors.cel}
                    </Typography>
                  </div>
                )}
              </div>

              <div className="flex-col">
                <TypographyAccessibility component="label" sx={sxLabel}>
                  E-mail
                </TypographyAccessibility>
                <TextField
                  id="email"
                  type="email"
                  variant="outlined"
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  sx={sxTextField}
                  fullWidth
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  InputProps={{
                    style: { ...accessibility.contrastStyle },
                  }}
                  inputProps={{
                    style: { color: accessibility.contrastMode ? "white" : "" },
                    'aria-describedby': formik.errors.email ? 'email-error' : undefined,
                  }}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="container-validation-message" id="email-error">
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      size="sm"
                      color="#D84141"
                    />
                    <Typography
                      style={accessibility.textStyleAccessibility}
                      id="errorEmail"
                      sx={sxValidationMessage}
                    >
                      {formik.errors.email}
                    </Typography>
                  </div>
                )}
              </div>
            </div>

            <Box sx={sxButtons}>
              <Button
                variant="outlined"
                sx={sxStyleBtn}
                onClick={() => {
                  formik.resetForm();
                  onClose();
                }}
              >
                <Typography style={{color: '#00995D'}}>Cancelar</Typography>
              </Button>
              <DefaultButton
                text="Salvar"
                type="submit"
                onClick={formik.submitForm}
              />
            </Box>
          </form>
        </Box>
      </ModalDefault>
    </>
  );
};

export default ModalEditContact;
